import { Dashboard } from 'components/Dashboard.jsx';
import { Request } from 'components/Request';
import { privateChatMessageHolder } from 'components/PrivateChatMessageHolder.jsx';
import { unreadPrivateMessagesChanged } from 'react/redux/actions/privateChat';
import { ChatRoom } from 'models/ChatRoom';
import { findMatchSettingsLoaded } from 'react/redux/actions/findMatch';
import Globals from 'components/Globals';

import * as connectionActions from 'react/redux/actions/connection';
import * as matchmakingActions from 'react/redux/actions/matchmaking';
import store from 'react/redux/store/store';
import { updatedMyUser } from 'react/redux/actions/user';
import { notificationsLoaded } from 'react/redux/actions/notifications';
import { bootstrapFields, shouldLoadPrivateChatsAtStartup } from 'app/ladderStartupEvents';
import { runBootstrap, BootstrapCancelled } from 'app/BootstrapLoader';
import { loadModCounts } from 'app/ModCountsLoader';
import { matchSearchListManager } from 'components/MatchSearchListManager';
import { cacheMyLadders, cacheMyUser, cachedMyLadders, cachedMyUser, clearCachedMyUser } from 'session/myUserCache';
import sendToLogIn from 'session/sendToLogIn';

// The single startup step; the connection notice shows it as loading, retrying or failed.
const BOOTSTRAP_STEP = 'bootstrap';

export default class StartupEventManager {
	static finishedOncePromise = new Promise((resolve) => {
		StartupEventManager.finishedOnce = resolve;
	});
	static userLoadedPromise = new Promise((resolve) => {
		StartupEventManager.userLoadedOnce = resolve;
	});

	constructor() {
		this.retryDelay = 4000;
		this.addedEvents = new Set([BOOTSTRAP_STEP]);
		this.eventList = new Map([[BOOTSTRAP_STEP, { name: BOOTSTRAP_STEP, active: false, failedLastAttempt: false, errors: 0 }]]);
	}

	static startupEventsFinished() {
		return StartupEventManager.finishedOncePromise;
	}

	static userLoaded() {
		return StartupEventManager.userLoadedPromise;
	}

	isCancelled() {
		return currentStartup !== this;
	}

	pushUpdate() {
		if(this.isCancelled()) {
			return;
		}
		store.dispatch(connectionActions.updateStartupEvents(this.addedEvents, this.eventList));
	}

	progress({ active, failedLastAttempt, errors, done }) {
		if(done) {
			this.eventList.delete(BOOTSTRAP_STEP);
		} else {
			this.eventList.set(BOOTSTRAP_STEP, { name: BOOTSTRAP_STEP, active, failedLastAttempt, errors });
		}
		this.pushUpdate();
	}

	runEvents() {
		this.pushUpdate();
		this.load()
			.then(() => {
				Dashboard.startupEventsResolve();
				StartupEventManager.finishedOnce();
				Globals.finishedStartup = true;
				console.log('Startup succeeded');
			})
			.catch((error) => {
				if(error instanceof BootstrapCancelled) {
					return;
				}
				const step = this.eventList.get(BOOTSTRAP_STEP);
				if(step) {
					step.active = false;
				}
				console.log('Startup failed');
				console.error(error);
			})
			.finally(() => {
				this.pushUpdate();
			});
	}

	async load() {
		if(!myUser.id) {
			console.warn('No logged in user, skipping startup events');
			this.eventList.delete(BOOTSTRAP_STEP);
			return;
		}
		Globals.finishedStartup = false;
		const page = startupPage;
		const loadPrivateChats = shouldLoadPrivateChatsAtStartup(store.getState().privateChat);
		const fields = withoutLoadedSections(page
			? page.fields({ loadPrivateChats })
			: bootstrapFields({ netplayPage: false, isReconnect: hasLoadedOnce, loadPrivateChats }));
		console.log('[startup] bootstrap', fields);
		const { embedded, revalidate } = knownUser();
		await runBootstrap({
			fields,
			embedded,
			revalidate,
			beforeFetch: page ? page.ready : undefined,
			fetchSections: fetchBootstrap,
			handlers: { ...sectionHandlers, my_user: (section) => afterGetUserGoing(section, page) },
			onProgress: (state) => this.progress(state),
			retryDelay: this.retryDelay,
			isCancelled: () => this.isCancelled(),
		});
		hasLoadedOnce = true;
		if(page) {
			page.loaded();
		}
		loadModCounts({
			user: myUser,
			fetchSections: fetchBootstrap,
			apply: performUpdate,
		});
	}
}

let hasLoadedOnce = false;
// Sections that stay current over the socket once they've loaded, so a later
// page or a reconnect doesn't ask for them again.
const loadedUserSections = new Set();
const withoutLoadedSections = (sections) => sections.filter((name) => !loadedUserSections.has(name));
// The latest run; an older one still retrying stops at its next attempt.
let currentStartup = null;
// The page in the page area that loads more than the site's sections (the
// netplay page, react/netplay/netplayStartupPage), or null:
// { fields({ loadPrivateChats }), ready(), userLoaded(user), loaded() }.
// ready() is waited on before the sections are requested.
let startupPage = null;

// Returns the release; a release from a page that was already replaced does nothing.
export function setStartupPage(page) {
	startupPage = page;
	return () => {
		if(startupPage === page) {
			startupPage = null;
		}
	};
}

/**
 * The user startup can show before the server answers: the one in memory
 * (printed by the layout, or loaded earlier this session), else the one this
 * browser cached for the player, which is still requested again.
 */
function knownUser() {
	const ladders = loadedUserSections.has('my_ladders') ? null : cachedMyLadders(myUser.id);
	const cachedSections = ladders ? { my_ladders: ladders } : {};
	const cachedNames = Object.keys(cachedSections);
	if(myUser.prefetchedData || hasLoadedOnce) {
		return { embedded: { sections: { my_user: { my_user: myUser }, ...cachedSections }, failed: {} }, revalidate: cachedNames };
	}
	const cached = cachedMyUser(myUser.id);
	if(cached) {
		return { embedded: { sections: { my_user: { my_user: cached }, ...cachedSections }, failed: {} }, revalidate: ['my_user', ...cachedNames] };
	}
	return { embedded: null, revalidate: [] };
}

/** GET bootstrap. Caches the user it sends; a logged-out answer goes to log in. */
function fetchBootstrap(sections) {
	return Request.apiv1Get('bootstrap', { fields: sections.join(',') }).then(
		(payload) => {
			const user = payload && payload.sections && payload.sections.my_user && payload.sections.my_user.my_user;
			if(user) {
				cacheMyUser(user);
			}
			const ladders = payload && payload.sections && payload.sections.my_ladders;
			if(ladders) {
				cacheMyLadders(myUser.id, ladders);
			}
			return payload;
		},
		(error) => {
			if(error && error.status === 401) {
				clearCachedMyUser();
				sendToLogIn();
			}
			throw error;
		}
	);
}

Globals.initialChat = null;

function applyChatRooms(response) {
	if(response.success) {
		Dashboard.dashboard.addClass('chat_rooms_loaded');
	}
	if(!response.chat_rooms) {
		Dashboard.performOpenSearchUpdate(response);
		return;
	}
	const responseChatRooms = response.chat_rooms;
	delete response.chat_rooms;
	_.each(responseChatRooms.chat_room, (chatRoom) => {
		if(chatRoom.last_active) {
			Globals.initialChat = chatRoom.id;
			return false;
		}
	});

	_.each(responseChatRooms.chat_room, (chatRoom, id) => {
		const chatRoomObject = {};
		chatRoomObject[id] = chatRoom;

		Dashboard.performOpenSearchUpdate({ chat_rooms: { chat_room: chatRoomObject } }); //Loads with no messages
	});
	Dashboard.chatRoomsLoaded = true;
}

function applyChatMessages(response) {
	ChatRoom.activeChats.forEach((chat) => {
		chat.chatMessages.setBrokenMessageState();
	});
	if(!response.success) {
		return;
	}
	const chatRoomId = Number.parseInt(response.last_active_chat || Globals.initialChat, 10);
	const chatRoom = ChatRoom.activeChats.get(chatRoomId);
	if(!chatRoom) {
		console.log('No active chat room for the startup messages', chatRoomId);
		return;
	}
	return chatRoom.chatMessages
		.loadInitialChatRoomMessagesFrom(response)
		.catch((error) => {
			console.log('Error with chat messages!', error);
			console.error(error);
		});
}

const performUpdate = (section) => Dashboard.performOpenSearchUpdate(section);

function applyMyLadders(section) {
	loadedUserSections.add('my_ladders');
	myUser.update(section);
	store.dispatch(updatedMyUser());
	// Search rows work out what the player's rating rules out as they render.
	matchSearchListManager.matchLists.forEach((list) => list.matchListChanged());
}

const sectionHandlers = {
	my_ladders: applyMyLadders,
	counts: performUpdate,
	site_notification: performUpdate,
	notifications: (section) => store.dispatch(notificationsLoaded(section)),
	locked_searches: performUpdate,
	chat_text: performUpdate,
	private_chats: (section) => {
		if(shouldLoadPrivateChatsAtStartup(store.getState().privateChat)) {
			privateChatMessageHolder.applyPrivateMessagePage(1, section);
		}
	},
	current_match: (section) => store.dispatch(matchmakingActions.applyCurrentMatch(section)),
	chat_rooms: applyChatRooms,
	chat_messages: applyChatMessages,
};

function afterGetUserGoing(response, page) {
	if(response.my_user) {
		store.dispatch(findMatchSettingsLoaded(response.my_user));
		myUser.update(response.my_user);
		// The store read the page's user before it had this; the dock's Chat badge counts it.
		if(response.my_user.unread_direct_conversation_count !== undefined) {
			store.dispatch(unreadPrivateMessagesChanged(Number(response.my_user.unread_direct_conversation_count) || 0));
		}
		delete response.my_user;
		StartupEventManager.userLoadedOnce(myUser);
		store.dispatch(updatedMyUser());
	} else {
		throw new Error('what happened');
	}

	if(page) {
		page.userLoaded(myUser);
	}

	Dashboard.updatePreferredGames();
	Dashboard.performOpenSearchUpdate(response);
	Dashboard.serverMessages.open();

	StartupEventManager.reconnectionTimeout = 5000;
}

/**
 * Requests sections startup left out and applies them the same way, for a part
 * of the page that needs them later (the dock's chat sheet: react/dock/chat).
 */
export function loadStartupSections(sections) {
	return runBootstrap({
		fields: withoutLoadedSections(sections),
		fetchSections: fetchBootstrap,
		handlers: sectionHandlers,
		maxErrors: 2,
	});
}

// Loads what the page needs at startup, and again after the socket
// reconnects (SocketConnection). session/siteSession runs it once per full
// page load, after connecting the socket, and again (force) when a page entry
// set a startup page. Reconnects are rate limited; a forced run is not.
export function runStartup({ force = false } = {}) {
	if(!force && !Globals.canCallGetUserGoingAgain) {
		console.log('rate limiting data retrieval');
		return;
	}
	Globals.canCallGetUserGoingAgain = false;
	setTimeout(function() {
		Globals.canCallGetUserGoingAgain = true;
	}, StartupEventManager.reconnectionTimeout - 1000);

	const startupEventManager = new StartupEventManager();
	currentStartup = startupEventManager;
	startupEventManager.runEvents();
}

Globals.canCallGetUserGoingAgain = true;
Globals.getUserGoing = () => runStartup();
StartupEventManager.reconnectionTimeout = 5000;
Globals.finishedStartup = false;